/**
 * web/src/components/HelpPayslipPreview.tsx
 * ヘルプスタッフ向け「ヘルプ給与明細」プレビュー＆印刷（シンプル版）。
 *
 * 通常アシスタントの PayslipPreview.tsx とは別コンポーネント
 * （payroll_records には混ぜず、one_time_work_entries を直接集計して表示する）。
 * 税務区分・正式書類名は未確定のため「ヘルプ給与明細」という社内呼称に統一し、
 * 法定の給与明細書・源泉徴収票・支払調書ではない旨を明記する。
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { HelpWorkEntry } from '../api/client'

function fmtCurrency(n: number | null | undefined): string {
  return `¥${Number(n ?? 0).toLocaleString('ja-JP')}`
}

function overtimeAmount(e: HelpWorkEntry): number {
  return Math.round((e.overtime_minutes * e.overtime_rate) / 60)
}

function entryAmount(e: HelpWorkEntry): number {
  return e.unit_rate + e.transport_fee + e.other_expense + overtimeAmount(e)
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function buildHelpPayslipHTML(staffName: string, month: string, entries: HelpWorkEntry[]): string {
  const [year, mon] = month.split('-')
  const total = entries.reduce((s, e) => s + entryAmount(e), 0)
  const rows = entries.map((e) => `
    <tr>
      <td>${escapeHtml(e.work_date)}</td>
      <td style="text-align:right">${fmtCurrency(e.unit_rate)}</td>
      <td style="text-align:right">${fmtCurrency(e.transport_fee)}</td>
      <td style="text-align:right">${fmtCurrency(e.other_expense)}</td>
      <td style="text-align:right">${fmtCurrency(overtimeAmount(e))}</td>
      <td style="text-align:right;font-weight:700">${fmtCurrency(entryAmount(e))}</td>
    </tr>
  `).join('')

  return `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>ヘルプ給与明細_${escapeHtml(staffName)}_${year}${mon}</title>
<style>
  body { font-family: -apple-system, "Hiragino Sans", sans-serif; padding: 32px; color: #1a1a1a; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  .sub { font-size: 12px; color: #666; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1px solid #ddd; padding: 8px 10px; }
  th { background: #f5f5f5; text-align: left; }
  tfoot td { font-weight: 700; background: #fafafa; }
  .note { margin-top: 20px; font-size: 11px; color: #888; line-height: 1.6; }
</style>
</head>
<body>
  <h1>ヘルプ給与明細</h1>
  <div class="sub">${year}年${Number(mon)}月分 — ${escapeHtml(staffName)} 様</div>
  <table>
    <thead>
      <tr>
        <th>勤務日</th>
        <th style="text-align:right">単価</th>
        <th style="text-align:right">交通費</th>
        <th style="text-align:right">その他経費</th>
        <th style="text-align:right">残業代</th>
        <th style="text-align:right">小計</th>
      </tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#999">対象月の勤務実績がありません</td></tr>'}</tbody>
    <tfoot>
      <tr><td colspan="5" style="text-align:right">合計</td><td style="text-align:right">${fmtCurrency(total)}</td></tr>
    </tfoot>
  </table>
  <div class="note">
    この明細は社内管理用の「ヘルプ給与明細」であり、法定の給与明細書・源泉徴収票・支払調書とは異なります。
    金額は管理者確定値（単価・交通費・その他経費・残業代）のみを集計しています。税務上の取り扱いは別途確認してください。
  </div>
</body>
</html>`
}

type Props = {
  helpStaffId: string
  helpStaffName: string
  month: string
  onClose: () => void
}

export function HelpPayslipPreview({ helpStaffId, helpStaffName, month, onClose }: Props) {
  const [entries, setEntries] = useState<HelpWorkEntry[] | null>(null)

  useEffect(() => {
    let active = true
    api.helpWorkEntries.list({ month, help_staff_id: helpStaffId })
      .then((list) => { if (active) setEntries(list.filter((e) => e.status !== 'cancelled')) })
      .catch(() => { if (active) setEntries([]) })
    return () => { active = false }
  }, [helpStaffId, month])

  const html = entries ? buildHelpPayslipHTML(helpStaffName, month, entries) : null

  const handlePrint = () => {
    if (!html) return
    const win = window.open('', '_blank', 'width=900,height=1200')
    if (!win) {
      alert('ポップアップがブロックされています。ブラウザの設定でポップアップを許可してください。')
      return
    }
    win.document.write(html)
    win.document.close()
    setTimeout(() => win.print(), 300)
  }

  const [year, mon] = month.split('-')

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: '92vw', maxHeight: '92vh', overflow: 'hidden' }}>
        <div className="modal-header">
          <div className="modal-title">
            ヘルプ給与明細プレビュー — {year}年{Number(mon)}月 / {helpStaffName}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ padding: 0, height: 'calc(92vh - 118px)', background: '#f3f4f6' }}>
          {!html ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>読み込み中...</div>
          ) : (
            <iframe
              title="ヘルプ給与明細プレビュー"
              srcDoc={html}
              style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#f3f4f6' }}
            />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>閉じる</button>
          <button className="btn btn-primary" onClick={handlePrint} disabled={!html}>
            🖨 印刷 / PDFとして保存
          </button>
        </div>
      </div>
    </div>
  )
}
